import { string, symbol } from 'zod';
import { HtmlElementWithFullscreen, DocumentWithFullscreen } from '../types/common';
import { LiquidationType, Order, OrderSide, PositionSide } from '../types/trade.d';
import { ReverseSide } from '../enum';
import { toNonExponential, VaildNumber } from './format';
import Big from 'big.js';
import isNumber from 'lodash/isNumber';
import { isFunction } from 'lodash';
import differenceInSeconds from 'date-fns/differenceInSeconds';

Big.NE = -20;

export const isBrowser = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  } else {
    return true;
  }
};

export const isFullscreen = (): boolean => {
  if (document.fullscreenElement) {
    return true;
  } else {
    return false;
  }
};

export function openFullscreen(elem: HtmlElementWithFullscreen) {
  if (elem.requestFullscreen) {
    elem.requestFullscreen();
  } else if (elem.webkitRequestFullscreen) {
    /* Safari */
    elem.webkitRequestFullscreen();
  } else if (elem.msRequestFullscreen) {
    /* IE11 */
    elem.msRequestFullscreen();
  }
}

export function safe_openFullscreen(elem: HTMLElement) {
  setTimeout(() => openFullscreen(elem), 0);
}

export function closeFullscreen() {
  const doc = document as DocumentWithFullscreen;
  if (doc.exitFullscreen) {
    doc.exitFullscreen();
  } else if (doc.webkitExitFullscreen) {
    /* Safari */
    doc.webkitExitFullscreen();
  } else if (doc.msExitFullscreen) {
    /* IE11 */
    doc.msExitFullscreen();
  }
}

export function safe_closeFullscreen() {
  setTimeout(() => closeFullscreen(), 0);
}

export const isOnline = (): boolean => {
  if (isBrowser()) {
    return window.navigator.onLine;
  } else {
    return false;
  }
};

export const watchDarkMode = (cb: (mode: 'dark' | 'light') => void): void => {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (e.matches) {
      // Dark
      cb('dark');
    } else {
      // Light
      cb('light');
    }
  });
};

export const isEamil = (account: string) => {
  return string().email().safeParse(account).success;
};

export function isLocalhostInSafari() {
  if (isBrowser()) {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && !isIOS;
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isSafari && isLocalhost;
  } else {
    return false;
  }
}

export function getPrecision(precision: string) {
  const number = toNonExponential(Number(precision));
  if (number) {
    if (number.includes('.')) {
      // 小数
      return number.slice(2).length;
    } else if (number === '1') {
      return 0;
    } else {
      return -(precision.length - 1);
    }
  } else {
    return 0;
  }
}

export function shortText(val: string, startSplit: number, endSplit: number, separator = '...') {
  const len = val.length;
  return startSplit + endSplit >= len
    ? val
    : val.slice(0, startSplit) + separator + (endSplit ? val.slice(-endSplit) : '');
}

/**
 * 对数字字符串格式化，不会进行任何预处理
 * 调用方需保证传入的数字是不带任何分隔符的标准数字字符串
 * @param value 数字字符串
 * @param digit 要保留的小数位数，超过的直接舍去
 * @param separator 是否需要千分位分隔符
 */
export function numberFormat(value: string, digit = 8, separator = true) {
  if (typeof value !== 'string') {
    return value;
  }
  const [int, decimal] = value.split('.');
  if (digit > 0) {
    if (separator) {
      const newInt = int.replace(/(\d)(?=(?:\d{3})+$)/g, '$1,');
      return `${newInt}${decimal ? '.' + decimal.slice(0, digit) : ''}`;
    } else {
      return `${int}${decimal ? '.' + decimal.slice(0, digit) : ''}`;
    }
  } else {
    return `${int}`;
  }
}

function roundToTick(price: string, tickSize: number): string {
  const priceNum = new Big(VaildNumber(price));
  const tickDecimals = Big(tickSize).toString().split('.')[1]?.length || 0;
  const tickMultiplier = new Big(10).pow(tickDecimals);
  const roundedNum = new Big(priceNum).div(tickSize).round(0, 3).times(tickSize);
  const roundedNumInt = roundedNum.times(tickMultiplier).round().div(tickMultiplier);
  return roundedNumInt.toFixed(tickDecimals);
}

/**
 * 当 tickerSize < 1 时，将价格精度规整到 tickerSize，向上取整
 * @param price 价格字符串
 * @param tickSize 格式为 0.1 ｜ 0.05 等
 * @returns
 */
export function formatNumberToTick(price: string, tickSize: number): string {
  if (price.includes('.') && tickSize < 1) {
    if (price.split('.')[1].length >= Big(tickSize).toString().split('.')[1].length) {
      // TODO 此处是大坑，千万别删。原因未知
      roundToTick(price, tickSize);
      price = roundToTick(price, tickSize);
    } else {
      return price;
    }
  }
  return price;
}

export function numberInputFormat(value: string, digit = 8, separator = true, tickSize?: number) {
  if (value === '' || value === null || value === undefined) {
    return value;
  }

  const formatedValue = stringReplacedPunctuation(value);

  let numberString = formatedValue.replace(/[^0-9.]/g, '');

  if (numberString.startsWith('0') && !numberString.includes('.')) {
    numberString = numberString.replace(/^0+/, '');
  }

  if (numberString.startsWith('.')) {
    numberString = numberString.replace('.', '0.');
  }

  if (Number(value) === 0) {
    if (digit <= 0) {
      return '';
    }
    if (value.includes('.')) {
      if (value.split('.')[1].length > digit - 1) {
        if (digit === 1) {
          return value.replace('.0', '.');
        } else {
          return value.replace('00', '0');
        }
      } else {
        return value;
      }
    } else {
      return value.replace('00', '0');
    }
  }

  if (digit <= 0 && numberString.includes('.')) {
    return numberString.split('.')[0];
  }

  // round to tickSize
  if (numberString.includes('.') && tickSize) {
    if (tickSize < 1) {
      if (numberString.split('.')[1].length >= toNonExponential(tickSize).split('.')[1].length) {
        numberString = roundToTick(numberString, tickSize);
      }
    }
  }

  if (
    ((numberString.indexOf('.') === numberString.length - 1 && numberString.endsWith('.')) ||
      ((numberString.match(/\./g) || []).length === 2 && numberString.endsWith('..'))) &&
    isNumber(Number(numberString))
  ) {
    return `${numberFormat(numberString, digit, separator)}.`;
  } else if (isNumber(Number(numberString))) {
    return numberFormat(numberString, digit, separator);
  } else {
    return '';
  }
}

/**
 * 移除 string 中的「,，。」分隔符
 */
export function stringReplacedPunctuation(value: string) {
  if (value === '' || value === null || value === undefined) {
    return value;
  }
  const result = value.replace(/[,，。]/g, '.');
  if (/^[^\.]*\.[^\.]*\.$/.test(result)) {
    return result.slice(0, -1);
  }
  return result;
}

export function firstCharUpper(str: string) {
  return typeof str === 'string' ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;
}

export function isMobile(userAgent: string) {
  const mobile = userAgent.match(
    /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i
  );
  return mobile != null;
}

export function isSafari() {
  let isSafari = 'Safari';
  if (isFunction(window.browser)) {
    isSafari = window.browser().browser;
  }
  if (isSafari === 'Safari') {
    return true;
  } else {
    return false;
  }
}

export function detectDeviceType(userAgent: string): string {
  if (/android/i.test(userAgent)) {
    return 'Android';
  } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return 'iOS';
  } else {
    return 'Pc';
  }
}

export function uniqueArray(arr: any[], uniId: string) {
  const res = new Map();
  return arr.filter((item) => !res.has(item[uniId]) && res.set(item[uniId], 1));
}

export function clearlAllIntervals(intervals: any) {
  for (const interval of intervals) {
    clearInterval(interval);
  }
}

export function formatStringToValidNumber(numberString: string): string {
  // 如果以小数点开头,在开头补零
  if (numberString.startsWith('.')) {
    return '0' + numberString;
  }

  // 包含小数点
  if (numberString.includes('.')) {
    const parts = numberString.split('.');
    const intPart = parts[0];
    const decPart = parts[1];

    // 整数部分有前导零,去掉前导零
    if (intPart.startsWith('0') && intPart.length > 1) {
      return intPart.replace(/^0+/, '') + '.' + decPart;
    } else {
      return numberString;
    }
  }

  return numberString;
}

export function convertSymbolForSpot(symbol: string) {
  let base = '',
    quote = '',
    sym = '';
  if (typeof symbol === 'string') {
    sym = symbol.replace('-', '/');
    [base = '', quote = ''] = sym.split('/');
  }
  return { base, quote, spotSymbol: sym };
}

export function getBrowserLocale() {
  if (navigator.languages != undefined) return navigator.languages[0];
  return navigator.language;
}

export function getBrowserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function parseUrlParam(url: string, key?: string) {
  const params: string[] | undefined = url?.split('?')?.[1]?.split('&');
  const obj: Record<string, string> = {};
  if (params) {
    params.forEach((item) => {
      const [key, value] = item.split('=');
      obj[key] = value || '';
    });
  }
  return key ? obj[key] || '' : obj;
}

function toRedirect(destination: string) {
  return {
    redirect: {
      destination,
      permanent: false,
    },
  } as const;
}

export function toRedirectNoPermission() {
  const env = process.env.NEXT_PUBLIC_MODE;
  return toRedirect(`https://${env === 'prod' ? '' : `${env}.`}blockfin.com/forbidden`);
}

export function toConvertSide(args: { position_side: PositionSide; order_side: OrderSide }): {
  side: Lowercase<PositionSide> | Lowercase<OrderSide>;
  isLongSide: boolean;
  reverseSide: Lowercase<PositionSide> | Lowercase<OrderSide>;
} {
  args.position_side = args.position_side || 'net';
  const lowerPosSide = args.position_side.toLowerCase() as Lowercase<PositionSide>;
  if (lowerPosSide === 'net') {
    const side = args.order_side.toLowerCase() as Lowercase<OrderSide>;
    return {
      side,
      isLongSide: side === 'buy',
      reverseSide: ReverseSide[side],
    };
  }
  return {
    side: lowerPosSide,
    isLongSide: lowerPosSide === 'long',
    reverseSide: ReverseSide[lowerPosSide],
  };
}

export function toConvertSideOfLang(args: { position_side: PositionSide; order_side: OrderSide; prefix?: string }) {
  args.position_side = args.position_side || 'net';
  const lowerPosSide = args.position_side.toLowerCase() as Lowercase<PositionSide>;
  const lowerOrdSide = args.order_side.toLowerCase() as Lowercase<OrderSide>;
  if (lowerPosSide === 'net') {
    return lowerOrdSide;
  }
  if ((lowerPosSide === 'long' && lowerOrdSide === 'buy') || (lowerPosSide === 'short' && lowerOrdSide === 'sell')) {
    return args.prefix ? `${args.prefix}_open_${lowerPosSide}` : `open_${lowerPosSide}`;
  }
  return args.prefix ? `${args.prefix}_close_${lowerPosSide}` : `close_${lowerPosSide}`;
}

export function diffTime(start: number, end: number) {
  const diff = differenceInSeconds(end, start);
  const days = Math.floor(diff / 86400)
    .toString()
    .padStart(2, '0');
  const hours = Math.floor((diff % 86400) / 3600)
    .toString()
    .padStart(2, '0');
  const minutes = Math.floor((diff % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (diff % 60).toString().padStart(2, '0');
  return { days, hours, minutes, seconds };
}

export function convertStringToArray(str: string | string[] = '') {
  return typeof str === 'string' ? (str ? str.replace(/；/g, ';').split(';') : []) : str;
}

export const isLiquidationOrder = (orderCategory: string) => {
  return orderCategory === 'liquidation' || orderCategory === 'partial_liquidation';
};

export const toLiquidationSide = (side: Lowercase<OrderSide>, liquidationType: LiquidationType) => {
  if (liquidationType === 'liquidation') {
    return side === 'buy' ? 'liquidationOfBuy' : 'liquidationOfSell';
  } else {
    return side === 'buy' ? 'partiallyLiquidationBuy' : 'partiallyLiquidationSell';
  }
};

export const getUserViewType = (cookieUserViewType?: string) => {
  const userViewType = cookieUserViewType || 'real-trading';
  return {
    userViewType: userViewType,
    isDemoTrading: Boolean(userViewType === 'demo-trading'),
  };
};
